'use client'

import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import Image from 'next/image'
import { cn } from '@segway/core'
import useEmblaCarousel from 'embla-carousel-react'

import { Arrow } from '@/components'
import {
  Carousel as UICarousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  type CarouselOptions,
  CarouselPrevious,
} from '@/ui/carousel/Carousel'

type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
export type CarouselPlugin = UseCarouselParameters[1]

interface CarouselProps extends React.HTMLAttributes<HTMLDivElement> {
  autoPlay?: boolean
  interval?: number
  loop?: boolean
  showArrows?: boolean
  showIndicators?: boolean
  showThumbnails?: boolean
  thumbnailsData?: string[]
  orientation?: 'horizontal' | 'vertical'
  arrowLeftContainerClassName?: string
  arrowLeftIconColor?: string
  arrowRightContainerClassName?: string
  arrowRightIconColor?: string
  arrowIconSize?: number
  arrowIconDisableClassName?: string
  indicatorsContainerClassName?: string
  indicatorsItemClassName?: string
  indicatorsActiveItemClassName?: string
  indicatorsAction?: 'hover' | 'click'
  carouselContentClassName?: string
  uiCarouselClassName?: string
  children: React.ReactNode
  onSlideChange?: (index: number) => void
  options?: CarouselOptions
  isHoverShowArrows?: boolean
  arrowHoverOption?: {
    btnClassName?: string
    iconColor?: string
  }
  plugins?: CarouselPlugin
  isArrowOut?: boolean
  thumbnailContainerClassName?: string
}

const Carousel = forwardRef<CarouselApi, CarouselProps>(
  (
    {
      autoPlay = true,
      interval = 3000,
      loop = false,
      showArrows = true,
      showIndicators = true,
      showThumbnails = false,
      thumbnailsData = [],
      orientation = 'horizontal',
      arrowLeftContainerClassName,
      arrowLeftIconColor = '#FFFFFF',
      arrowRightContainerClassName,
      arrowRightIconColor = '#FFFFFF',
      arrowIconSize = 24,
      arrowIconDisableClassName = '',
      indicatorsContainerClassName,
      indicatorsItemClassName,
      indicatorsActiveItemClassName,
      carouselContentClassName,
      className,
      uiCarouselClassName,
      children,
      onSlideChange,
      options,
      isHoverShowArrows = false,
      arrowHoverOption = {
        btnClassName: 'bg-[rgba(0,0,0,0.3)]',
        iconColor: '#FFFFFF',
      },
      plugins,
      indicatorsAction = 'hover',
      isArrowOut = false,
      thumbnailContainerClassName = '',
      ...props
    },
    ref,
  ) => {
    const [api, setApi] = useState<CarouselApi>()
    const [current, setCurrent] = useState(0)
    const [count, setCount] = useState(0)
    const tabsRef = useRef<HTMLDivElement>(null)
    const tabRefs = useRef<(HTMLDivElement | null)[]>([])

    const [isHover, setIsHover] = useState(false)
    const [isPrevHover, setIsPrevHover] = useState(false)
    const [isNextHover, setIsNextHover] = useState(false)

    useImperativeHandle(ref, () => api, [api])

    useEffect(() => {
      if (!api) return

      setCount(api.scrollSnapList().length)
      setCurrent(api.selectedScrollSnap())

      api.on('select', () => {
        setCurrent(api.selectedScrollSnap())
        if (onSlideChange) {
          onSlideChange(api.selectedScrollSnap())
        }
      })
    }, [api, onSlideChange])

    useEffect(() => {
      if (!api || !autoPlay) return

      const intervalId = setInterval(() => {
        if (document.visibilityState === 'visible') {
          api.scrollNext()
        }
      }, interval)

      return () => clearInterval(intervalId)
    }, [api, autoPlay, interval])

    useEffect(() => {
      if (showThumbnails && tabRefs.current[current]) {
        const tabElement = tabRefs.current[current]
        const container = tabsRef.current
        if (!tabElement || !container) return
        const containerRect = container.getBoundingClientRect()
        const tabRect = tabElement.getBoundingClientRect()

        if (tabRect.left < containerRect.left) {
          container.scrollLeft -= containerRect.left - tabRect.left
        } else if (tabRect.right > containerRect.right) {
          container.scrollLeft += tabRect.right - containerRect.right
        }
      }
    }, [showThumbnails, current])

    return (
      <div className={cn('relative', className)} {...props}>
        <UICarousel
          className={uiCarouselClassName}
          onMouseEnter={() => setIsHover(true)}
          onMouseLeave={() => setIsHover(false)}
          setApi={setApi}
          orientation={orientation}
          plugins={plugins}
          opts={{
            align: 'start',
            slidesToScroll: 1,
            loop: loop,
            ...options,
          }}>
          <CarouselContent className={carouselContentClassName}>{children}</CarouselContent>
          {showArrows && (!isHoverShowArrows || isHover) && (
            <>
              <CarouselPrevious
                className={cn(
                  'absolute left-11 top-1/2 z-[5] flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/40 focus:bg-[rgba(0,0,0,0.65)] focus:outline-none active:bg-[rgba(0,0,0,0.65)]',
                  arrowLeftContainerClassName,
                  isPrevHover && arrowHoverOption?.btnClassName,
                  isHoverShowArrows && (isPrevHover || isHover ? 'opacity-100' : 'opacity-0'),
                )}
                onMouseEnter={() => setIsPrevHover(true)}
                onMouseLeave={(e) => {
                  setIsPrevHover(false)
                  e.currentTarget.blur()
                }}
                disableClassName={cn('!bg-[rgba(0,0,0,0.15)]', arrowIconDisableClassName)}>
                <Arrow
                  width={arrowIconSize}
                  height={arrowIconSize}
                  rotate={180}
                  color={isPrevHover ? arrowHoverOption?.iconColor : arrowLeftIconColor}
                />
              </CarouselPrevious>

              <CarouselNext
                className={cn(
                  'absolute right-11 top-1/2 z-[5] flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/40 focus:bg-[rgba(0,0,0,0.65)] focus:outline-none active:bg-[rgba(0,0,0,0.65)]',
                  arrowRightContainerClassName,
                  isNextHover && arrowHoverOption?.btnClassName,
                  isHoverShowArrows && (isNextHover || isHover ? 'opacity-100' : 'opacity-0'),
                )}
                onMouseEnter={() => setIsNextHover(true)}
                onMouseLeave={(e) => {
                  e.currentTarget.blur()
                  setIsNextHover(false)
                }}
                disableClassName={cn('!bg-[rgba(0,0,0,0.15)]', arrowIconDisableClassName)}>
                <Arrow
                  width={arrowIconSize}
                  height={arrowIconSize}
                  color={isNextHover ? arrowHoverOption?.iconColor : arrowRightIconColor}
                />
              </CarouselNext>
            </>
          )}
          {showArrows && isArrowOut && (
            <>
              <div
                className={cn(
                  'absolute left-11 top-1/2 flex h-full w-[100px] -translate-y-1/2 cursor-pointer',
                  arrowLeftContainerClassName,
                )}
                onMouseEnter={() => setIsPrevHover(true)}
                onMouseLeave={() => setIsPrevHover(false)}></div>
              <div
                className={cn(
                  'absolute right-11 top-1/2 flex h-full w-[100px] -translate-y-1/2 cursor-pointer',
                  arrowRightContainerClassName,
                )}
                onMouseEnter={() => setIsPrevHover(true)}
                onMouseLeave={() => setIsPrevHover(false)}></div>
            </>
          )}
          {((showIndicators && showThumbnails && !isHover) ||
            (showIndicators && !showThumbnails)) && (
            <div
              className={cn(
                'absolute flex gap-3',
                orientation === 'horizontal'
                  ? 'bottom-2 left-1/2 -translate-x-1/2'
                  : 'left-2 top-1/2 -translate-y-1/2 flex-col',
                indicatorsContainerClassName,
              )}>
              {Array.from({ length: count }).map((_, index) => (
                <button
                  key={index}
                  className={cn(
                    'h-2 rounded-full transition-all',
                    orientation === 'horizontal' ? 'w-[30px]' : 'h-[30px] w-2',
                    index === current ? 'bg-primary' : 'w-2 bg-gray-300',
                    index === current ? indicatorsActiveItemClassName : indicatorsItemClassName,
                  )}
                  onMouseEnter={
                    indicatorsAction === 'hover'
                      ? () => {
                          api?.scrollTo(index)
                        }
                      : () => {}
                  }
                  onClick={
                    indicatorsAction === 'click'
                      ? () => {
                          api?.scrollTo(index)
                        }
                      : () => {}
                  }
                />
              ))}
            </div>
          )}

          <div
            ref={tabsRef}
            className={cn(
              thumbnailContainerClassName,
              'horizontal-scrollable-container absolute flex max-w-[640px] gap-2 overflow-x-auto',
              orientation === 'horizontal'
                ? 'bottom-6 left-1/2 -translate-x-1/2'
                : 'left-2 top-1/2 -translate-y-1/2 flex-col',
              isHover && showThumbnails ? '' : 'hidden',
            )}>
            {thumbnailsData.map((item, index) => (
              <div
                ref={(el: HTMLDivElement | null) => {
                  tabRefs.current[index] = el
                }}
                key={index}
                onMouseEnter={() => api?.scrollTo(index)}
                className={cn(
                  'relative flex h-[72px] w-[72px] flex-none cursor-pointer items-center justify-center overflow-hidden rounded-md bg-black/5',
                  index === current ? 'border border-black' : '',
                )}>
                <Image src={item || ''} alt="thumbnail" fill style={{ objectFit: 'cover' }} />
              </div>
            ))}
          </div>
        </UICarousel>
      </div>
    )
  },
)

Carousel.displayName = 'Carousel'

export { CarouselItem }

export default Carousel
